import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Verify2FAPage() {
  const [code, setCode] = useState('');
  const [method, setMethod] = useState('totp');
  const [loading, setLoading] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const tempToken = localStorage.getItem('temp_token');

  if (!tempToken) {
    navigate('/login');
    return null;
  }

  const sendSmsOtp = async () => {
    try {
      await axios.post(
        `${API}/auth/send-sms-otp`,
        {},
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );
      toast.success('SMS OTP sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/auth/verify-2fa`,
        { code, method },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      localStorage.removeItem('temp_token');
      await login(response.data.access_token);
      toast.success('2FA verified successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-secondary">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md"
      >
        <Card data-testid="verify-2fa-card">
          <CardHeader>
            <CardTitle data-testid="verify-2fa-title">Two-Factor Authentication</CardTitle>
            <CardDescription>Enter your verification code to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={method} onValueChange={setMethod} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="totp" data-testid="totp-tab">Authenticator App</TabsTrigger>
                <TabsTrigger value="sms" data-testid="sms-tab">SMS</TabsTrigger>
              </TabsList>
              <TabsContent value="totp" className="space-y-4 mt-4">
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="totp-code">6-Digit Code</Label>
                    <Input
                      id="totp-code"
                      type="text"
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      maxLength={6}
                      required
                      data-testid="totp-code-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="verify-totp-button">
                    {loading ? 'Verifying...' : 'Verify'}
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="sms" className="space-y-4 mt-4">
                <Button onClick={sendSmsOtp} variant="outline" className="w-full" data-testid="send-sms-button">
                  Send SMS Code
                </Button>
                <form onSubmit={handleVerify} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="sms-code">6-Digit Code</Label>
                    <Input
                      id="sms-code"
                      type="text"
                      placeholder="000000"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      maxLength={6}
                      required
                      data-testid="sms-code-input"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="verify-sms-button">
                    {loading ? 'Verifying...' : 'Verify'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
